import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import { withBase } from '../config/api'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper
} from '@mui/material'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  primaryButtonStyle,
  modalPrimaryButtonStyle,
  alternativeButtonStyle
} from '../styles/buttonStyles'
import SupportTicketModal from './SupportTicketModal'
import logo from '../assets/logo-dark.png'
import brandIcon from '../assets/brand_logo.png'
import showPwd from '../assets/images/icons/show_pwd.png'
import hidePwd from '../assets/images/icons/hide_pwd.png'
import './SupportTickets.css'

const ALLOWED_ROLES = ['admin', 'operator']

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'New' },
  { value: 'pending', label: 'Pending' },
  { value: 'customer_responded', label: 'Customer responded' },
  { value: 'waiting_customer', label: 'Waiting on customer' },
  { value: 'closed', label: 'Closed' }
]

const PAGE_SIZE = 25

const INITIAL_COLUMNS = [
  { key: 'ticket_number', label: 'Ticket' },
  { key: 'status', label: 'Stav' },
  { key: 'subject', label: 'Predmet' },
  { key: 'email', label: 'Email' },
  { key: 'created_at', label: 'Dátum vytvorenia' },
  { key: 'updated_at', label: 'Posledná aktivita' },
  { key: 'support_agent', label: 'Support agent' }
]

const formatDate = value => {
  if (!value) return '-'

  const date = new Date(value)

  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleString('sk-SK')
}

function SortableHeaderCell({
  col,
  orderBy,
  order,
  handleSort,
  colWidths,
  handleMouseDown
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: col.key })

  const width = colWidths[col.key]

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(width
      ? {
          width,
          minWidth: width,
          maxWidth: width
        }
      : {}),
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#c8e1e1',
    fontWeight: 'bold'
  }

  return (
    <TableCell
      ref={setNodeRef}
      style={style}
      sortDirection={orderBy === col.key ? order : false}
    >
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: 'grab',
          marginRight: 6,
          display: 'inline-block',
          color: '#999',
          touchAction: 'none'
        }}
      >
        ⠿
      </span>

      <TableSortLabel
        active={orderBy === col.key}
        direction={orderBy === col.key ? order : 'asc'}
        onClick={() => handleSort(col.key)}
      >
        {col.label}
      </TableSortLabel>

      <div
        onMouseDown={e => handleMouseDown(e, col.key)}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 5,
          cursor: 'col-resize',
          zIndex: 3
        }}
      />
    </TableCell>
  )
}

const SupportLogin = ({ onClose, onSuccess }) => {
  const { t, i18n } = useTranslation()
  const { refreshUser } = useAuth()

  const [loginForm, setLoginForm] = useState({
    email: '',
    password: '',
    mfaCode: ''
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showMFA, setShowMFA] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loginMessage, setLoginMessage] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)

  const updateLoginForm = (key, value) => {
    setLoginForm(prev => ({
      ...prev,
      [key]: value
    }))
  }

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key !== 'Escape' || isLoading) return

      if (showMFA) {
        setShowMFA(false)
        setLoginForm(prev => ({
          ...prev,
          mfaCode: ''
        }))
        setLoginMessage(null)
      } else {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, showMFA, isLoading])

  const buttonEvents = id => ({
    onMouseEnter: () => setHoverBtn(id),
    onMouseLeave: () => {
      setHoverBtn(null)
      setPressedBtn(null)
    },
    onMouseDown: () => setPressedBtn(id),
    onMouseUp: () => setPressedBtn(null)
  })

  const login = async event => {
    if (event) event.preventDefault()
    if (isLoading) return

    if (!loginForm.email) {
      setLoginMessage({
        message: t('EMAIL_REQUIRED'),
        type: 'error'
      })
      return
    }

    if (!loginForm.password) {
      setLoginMessage({
        message: t('PASSWORD_REQUIRED'),
        type: 'error'
      })
      return
    }

    setIsLoading(true)
    setLoginMessage(null)

    try {
      const response = await fetch(withBase('/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language,
          'X-Login-Context': 'support'
        },
        credentials: 'include',
        body: JSON.stringify(loginForm)
      })

      const data = await response.json()

      if (response.status === 403) {
        setLoginMessage({
          message: data?.message || 'Prístup odmietnutý.',
          type: 'error'
        })
        return
      }

      if (data?.mfaRequired) {
        setShowMFA(true)

        if (loginForm.mfaCode) {
          const translated = t('MFA_CODE_INVALID')

          setLoginMessage({
            message: translated === 'MFA_CODE_INVALID'
              ? 'Nesprávny MFA kód'
              : translated,
            type: 'error'
          })
        }

        return
      }

      if (!response.ok) {
        setLoginMessage({
          message: data?.message || 'Error',
          type: 'error'
        })
        return
      }

      await refreshUser()
      onSuccess()
    } catch {
      setLoginMessage({
        message: t('SERVER-ERROR') || 'Server nie je dostupný',
        type: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const forgotPassword = async () => {
    if (isLoading) return

    if (!loginForm.email) {
      setLoginMessage({
        message: t('EMAIL_REQUIRED'),
        type: 'error'
      })
      return
    }

    setIsLoading(true)
    setLoginMessage(null)

    try {
      const response = await fetch(
        withBase('/auth/forgot-password'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language
          },
          body: JSON.stringify({
            email: loginForm.email
          })
        }
      )

      const data = await response.json()

      setLoginMessage({
        message: data?.message || 'Error',
        type: response.ok ? 'success' : 'error'
      })
    } catch {
      setLoginMessage({
        message: t('SERVER-ERROR') || 'Server nie je dostupný',
        type: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const inputStyle = {
    width: '100%',
    height: 44,
    fontSize: 15,
    padding: '0 18px',
    marginBottom: 12,
    border: '1.5px solid #1f8d7b',
    background: '#ffffff',
    color: '#000000',
    boxSizing: 'border-box'
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 4000
      }}
    >
      <div
        style={{
          background: '#122629',
          padding: 20,
          border: '1px solid #107c6b',
          borderRadius: 12,
          boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
          width: 'clamp(320px, 30vw, 420px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <h3
          style={{
            fontSize: 28,
            margin: 0,
            marginBottom: 24,
            color: '#d9ffe4'
          }}
        >
          {showMFA ? t('MFA_AUTH_APP') : t('LOGIN')}
        </h3>

        {loginMessage && (
          <div
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: 12,
              marginBottom: 12,
              borderRadius: 6,
              color: loginMessage.type === 'error'
                ? '#ffb6b6'
                : '#d9ffe4',
              backgroundColor: loginMessage.type === 'error'
                ? '#4a2020'
                : '#174236',
              whiteSpace: 'pre-wrap'
            }}
          >
            {loginMessage.message}
          </div>
        )}

        <form
          onSubmit={login}
          style={{ width: '100%' }}
        >
          {!showMFA ? (
            <>
              <input
                autoFocus
                disabled={isLoading}
                placeholder={t('EMAIL')}
                value={loginForm.email}
                onChange={e =>
                  updateLoginForm('email', e.target.value)
                }
                style={inputStyle}
              />

              <div style={{ position: 'relative' }}>
                <input
                  disabled={isLoading}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('PASSWORD')}
                  value={loginForm.password}
                  onChange={e =>
                    updateLoginForm('password', e.target.value)
                  }
                  style={{
                    ...inputStyle,
                    marginBottom: 16,
                    paddingRight: 45,
                    backgroundImage: `url(${
                      showPassword ? hidePwd : showPwd
                    })`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '20px'
                  }}
                />

                <button
                  type="button"
                  aria-label={
                    showPassword
                      ? 'Hide password'
                      : 'Show password'
                  }
                  onClick={() =>
                    setShowPassword(prev => !prev)
                  }
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 44,
                    height: 44,
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </>
          ) : (
            <input
              autoFocus
              disabled={isLoading}
              placeholder={t('MFA_CODE')}
              value={loginForm.mfaCode}
              onChange={e =>
                updateLoginForm('mfaCode', e.target.value)
              }
              style={{
                ...inputStyle,
                marginBottom: 16
              }}
            />
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              ...modalPrimaryButtonStyle(
                pressedBtn,
                'login-submit',
                hoverBtn === 'login-submit'
              ),
              width: '100%',
              marginBottom: 8
            }}
            {...buttonEvents('login-submit')}
          >
            {isLoading ? '...' : t('LOGIN')}
          </button>
        </form>

        {!showMFA && (
          !showForgot ? (
            <button
              type="button"
              disabled={isLoading}
              style={{
                ...alternativeButtonStyle(
                  pressedBtn,
                  'forgot',
                  hoverBtn === 'forgot'
                ),
                width: '100%',
                marginBottom: 8
              }}
              {...buttonEvents('forgot')}
              onClick={() => setShowForgot(true)}
            >
              {t('FORGOT_PASSWORD')}
            </button>
          ) : (
            <button
              type="button"
              disabled={isLoading}
              style={{
                ...modalPrimaryButtonStyle(
                  pressedBtn,
                  'reset',
                  hoverBtn === 'reset'
                ),
                width: '100%',
                marginBottom: 8
              }}
              {...buttonEvents('reset')}
              onClick={forgotPassword}
            >
              {t('SEND_RESET_LINK')}
            </button>
          )
        )}

        <button
          type="button"
          disabled={isLoading}
          style={{
            ...alternativeButtonStyle(
              pressedBtn,
              'close',
              hoverBtn === 'close'
            ),
            width: '100%'
          }}
          {...buttonEvents('close')}
          onClick={() => {
            if (showMFA) {
              setShowMFA(false)
              updateLoginForm('mfaCode', '')
              setLoginMessage(null)
            } else {
              onClose()
            }
          }}
        >
          {showMFA ? t('CANCEL') : t('CLOSE')}
        </button>
      </div>
    </div>
  )
}

const SupportTickets = () => {
  const { t, i18n } = useTranslation()
  const {
    user,
    loading: authLoading,
    logout
  } = useAuth()

  const [viewport, setViewport] = useState(window.innerWidth)
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const [tickets, setTickets] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showLogin, setShowLogin] = useState(false)
  const [loginDismissed, setLoginDismissed] = useState(false)
  const [accessDenied, setAccessDenied] = useState(false)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)

  const [orderBy, setOrderBy] = useState('ticket_number')
  const [order, setOrder] = useState('desc')
  const [columns, setColumns] = useState(INITIAL_COLUMNS)
  const [colWidths, setColWidths] = useState({})

  const agentNamesRef = useRef(null)
  const refreshInProgressRef = useRef(false)

  const sensors = useSensors(useSensor(PointerSensor))

  const authorized = Boolean(
    user && ALLOWED_ROLES.includes(user.role)
  )

  useEffect(() => {
    const onResize = () => {
      setViewport(window.innerWidth)
    }

    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  useEffect(() => {
    if (authLoading) return

    if (user && !ALLOWED_ROLES.includes(user.role)) {
      setShowLogin(false)
      setAccessDenied(true)
      setTickets([])
      setSelectedId(null)
      return
    }

    if (authorized) {
      setShowLogin(false)
      setLoginDismissed(false)
      setAccessDenied(false)
    } else if (!loginDismissed && !accessDenied) {
      setShowLogin(true)
    }
  }, [
    user,
    authLoading,
    authorized,
    loginDismissed,
    accessDenied
  ])

  const layout = {
    headerHeight: 100,
    logoHeight: viewport >= 1920 ? 80 : 70,
    fontSize: 35,
    paddingX: 40
  }

  const lang = i18n.language?.split('-')[0]

  const loadTickets = useCallback(async ({ silent = false } = {}) => {
    if (silent && refreshInProgressRef.current) return

    if (!authorized) {
      setTickets([])
      return
    }

    refreshInProgressRef.current = true

    if (!silent) {
      setLoading(true)
      setError('')
    }

    try {
      const allTickets = []
      let page = 1
      let total = 0

      do {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(PAGE_SIZE)
        })

        if (status !== 'all') {
          params.set('status', status)
        }

        const response = await fetchWithAuth(
          `/support/tickets?${params.toString()}`,
          { cache: 'no-store' }
        )

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const data = await response.json()
        const rows = Array.isArray(data.rows)
          ? data.rows
          : []

        allTickets.push(...rows)
        total = Number(data.count) || allTickets.length

        if (rows.length < PAGE_SIZE) {
          break
        }

        page += 1
      } while (allTickets.length < total)

      if (agentNamesRef.current === null) {
        const agentsResponse = await fetchWithAuth(
          '/support/agents',
          { cache: 'no-store' }
        )

        if (!agentsResponse.ok) {
          throw new Error(`HTTP ${agentsResponse.status}`)
        }

        const agentsData = await agentsResponse.json()
        const agents = Array.isArray(agentsData)
          ? agentsData
          : []

        agentNamesRef.current = new Map(
          agents.map(agent => [
            String(agent.id),
            `${agent.meno || ''} ${agent.priezvisko || ''}`.trim()
          ])
        )
      }

      const agentNames = agentNamesRef.current

      setTickets(
        allTickets.map(ticket => ({
          ...ticket,
          support_agent: ticket.assigned_to == null
            ? '-'
            : agentNames.get(String(ticket.assigned_to)) || '-'
        }))
      )
    } catch (err) {
      if (!silent) {
        setTickets([])
        setError(
          `Nepodarilo sa načítať tickety: ${err.message}`
        )
      } else {
        console.error('Support tickets refresh failed:', err)
      }
    } finally {
      refreshInProgressRef.current = false

      if (!silent) {
        setLoading(false)
      }
    }
  }, [status, authorized])

  useEffect(() => {
    loadTickets()

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadTickets({ silent: true })
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [loadTickets])

  const changeFilter = nextStatus => {
    setStatus(nextStatus)
    setSelectedId(null)
    setSearch('')
  }

  const getTicketValue = (ticket, key) => {
    switch (key) {
      case 'ticket_number':
        return ticket.ticket_number || ticket.id

      case 'status':
        return FILTERS.find(
          f => f.value === ticket.status
        )?.label || ticket.status || '-'

      case 'subject':
        return ticket.subject || '-'

      case 'email':
        return ticket.email || '-'

      case 'created_at':
        return formatDate(
          ticket.created_at || ticket.createdAt
        )

      case 'updated_at':
        return formatDate(
          ticket.updated_at || ticket.updatedAt
        )

      case 'support_agent':
        return ticket.support_agent || '-'

      default:
        return '-'
    }
  }

  const processedTickets = useMemo(() => {
    const q = search.trim().toLowerCase()

    const filtered = tickets.filter(ticket => {
      const value = [
        ticket.ticket_number,
        ticket.subject,
        ticket.email,
        ticket.support_agent
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return value.includes(q)
    })

    const getSortValue = ticket => {
      if (orderBy === 'created_at') {
        return new Date(
          ticket.created_at || ticket.createdAt || 0
        ).getTime()
      }

      if (orderBy === 'updated_at') {
        return new Date(
          ticket.updated_at || ticket.updatedAt || 0
        ).getTime()
      }

      return ticket[orderBy] ?? ''
    }

    return [...filtered].sort((a, b) => {
      const av = getSortValue(a)
      const bv = getSortValue(b)

      let cmp

      if (
        typeof av === 'number' &&
        typeof bv === 'number'
      ) {
        cmp = av - bv
      } else {
        cmp = String(av).localeCompare(
          String(bv),
          'sk',
          {
            numeric: true,
            sensitivity: 'base'
          }
        )
      }

      return order === 'asc' ? cmp : -cmp
    })
  }, [tickets, search, orderBy, order])

  const handleSort = key => {
    if (orderBy === key) {
      setOrder(prev =>
        prev === 'asc' ? 'desc' : 'asc'
      )
    } else {
      setOrderBy(key)
      setOrder('asc')
    }
  }

  const handleMouseDown = (event, key) => {
    event.preventDefault()
    event.stopPropagation()

    const th = event.currentTarget.closest('th')
    const startX = event.clientX

    const startWidth = th
      ? th.getBoundingClientRect().width
      : colWidths[key] || 150

    const onMouseMove = moveEvent => {
      const delta = moveEvent.clientX - startX
      const newWidth = Math.max(
        50,
        startWidth + delta
      )

      setColWidths(prev => ({
        ...prev,
        [key]: newWidth
      }))
    }

    const onMouseUp = () => {
      window.removeEventListener(
        'mousemove',
        onMouseMove
      )

      window.removeEventListener(
        'mouseup',
        onMouseUp
      )
    }

    window.addEventListener(
      'mousemove',
      onMouseMove
    )

    window.addEventListener(
      'mouseup',
      onMouseUp
    )
  }

  const handleDragEnd = event => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    setColumns(prev => {
      const oldIndex = prev.findIndex(
        col => col.key === active.id
      )

      const newIndex = prev.findIndex(
        col => col.key === over.id
      )

      if (oldIndex === -1 || newIndex === -1) {
        return prev
      }

      const reordered = [...prev]
      const [moved] = reordered.splice(oldIndex, 1)

      reordered.splice(newIndex, 0, moved)

      return reordered
    })
  }

  const handleLogout = async () => {
    setSelectedId(null)
    setTickets([])
    setLoginDismissed(false)
    setAccessDenied(false)

    agentNamesRef.current = null

    await logout()

    setShowLogin(true)
  }

  const openLogin = () => {
    setAccessDenied(false)
    setLoginDismissed(false)
    setShowLogin(true)
  }

  return (
    <div
      className="support-page"
      style={{
        backgroundColor: '#3BC6C4',
        backgroundImage: `
          linear-gradient(
            rgba(59, 198, 196, 0.9),
            rgba(59, 198, 196, 0.9)
          ),
          url('/pattern_5.png')
        `,
        backgroundRepeat: 'repeat',
        backgroundSize: '400px 400px'
      }}
    >
      <header
        className="support-header"
        style={{
          height: layout.headerHeight,
          minHeight: layout.headerHeight,
          padding: `0 ${layout.paddingX}px`
        }}
      >
        <div
          className="support-header-pattern"
          style={{
            backgroundImage: "url('/pattern_6.png')",
            backgroundRepeat: 'repeat',
            backgroundSize: '400px 400px',
            opacity: 0.1
          }}
        />

        <div
          className="support-header-left"
          style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: layout.fontSize,
            fontWeight: 700,
            marginLeft: -20,
            marginTop: layout.headerHeight * 0.3,
            position: 'relative',
            zIndex: 1,
            filter: 'drop-shadow(0 10px 8px rgba(0,0,0,0.4))'
          }}
        >
          <img
            src={brandIcon}
            alt="EnerqiQ"
            style={{
              height: layout.fontSize + 5,
              width: 'auto',
              marginRight: 8
            }}
          />

          <span style={{ color: '#b3b0b5' }}>
            {t('SLOGAN')}{' '}
          </span>

          <span
            style={{
              color: '#079086',
              marginLeft: 10
            }}
          >
            iQ
          </span>

          {lang === 'hu' && (
            <span style={{ color: '#1e3f44' }}>
              {t('POST_SLOGAN_HU')}
            </span>
          )}
        </div>

        <div className="support-header-logo">
          <img
            src={logo}
            alt="EnerqiQ"
            style={{
              height: layout.logoHeight
            }}
          />
        </div>

        <div className="support-header-right">
          <button
            type="button"
            style={primaryButtonStyle(
              pressedBtn,
              'auth',
              hoverBtn === 'auth'
            )}
            onMouseEnter={() => setHoverBtn('auth')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('auth')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={
              authorized
                ? handleLogout
                : openLogin
            }
          >
            {authorized
              ? t('LOGOUT')
              : t('LOGIN')}
          </button>
        </div>
      </header>

      <main
        className="support-content"
        style={{
          backgroundColor: 'transparent'
        }}
      >
        <aside className="support-sidebar">
          {FILTERS.map(filter => (
            <button
              type="button"
              key={filter.value}
              className={`support-filter-button ${
                status === filter.value
                  ? 'active'
                  : ''
              }`}
              onClick={() =>
                changeFilter(filter.value)
              }
            >
              {filter.label}
            </button>
          ))}
        </aside>

        <section className="support-center">
          {accessDenied && (
            <div className="support-error">
              Prístup odmietnutý. Nemáte oprávnenie
              na používanie podpory.
            </div>
          )}

          {authorized && (
            <div className="support-box">
              <div className="support-toolbar">
                <h2>
                  {
                    FILTERS.find(
                      f => f.value === status
                    )?.label
                  }
                </h2>

                <input
                  type="search"
                  className="support-search"
                  placeholder="Hľadať ticket, predmet alebo email..."
                  value={search}
                  onChange={e =>
                    setSearch(e.target.value)
                  }
                />
              </div>

              {error && (
                <div className="support-error">
                  {error}
                </div>
              )}

              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  overflow: 'auto',
                  border: '1px solid #c7c7c7',
                  borderRadius: '10px',
                  boxShadow: 'none'
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    tableLayout: 'auto',
                    width: 'max-content',
                    minWidth: '100%',
                    '& thead th': {
                      fontWeight: 'bold',
                      backgroundColor: '#c8e1e1',
                      whiteSpace: 'nowrap',
                      fontSize: 12,
                      padding: '6px 8px'
                    },
                    '& tbody td': {
                      fontSize: 12,
                      padding: '5px 8px',
                      whiteSpace: 'nowrap',
                      height: 29,
                      boxSizing: 'border-box'
                    },
                    '& .MuiTableSortLabel-root': {
                      fontSize: 12
                    }
                  }}
                >
                  <TableHead>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={columns.map(col => col.key)}
                        strategy={
                          horizontalListSortingStrategy
                        }
                      >
                        <TableRow>
                          {columns.map(col => (
                            <SortableHeaderCell
                              key={col.key}
                              col={col}
                              orderBy={orderBy}
                              order={order}
                              handleSort={handleSort}
                              colWidths={colWidths}
                              handleMouseDown={handleMouseDown}
                            />
                          ))}
                        </TableRow>
                      </SortableContext>
                    </DndContext>
                  </TableHead>

                  <TableBody>
                    {processedTickets.map(ticket => (
                      <TableRow
                        key={ticket.id}
                        hover
                        onClick={() => {
                          setSelectedId(ticket.id)
                          setTickets(prev =>
                            prev.map(t =>
                              t.id === ticket.id
                                ? { ...t, is_unread: false }
                                : t
                            )
                          )
                        }}
                        sx={{
                          cursor: 'pointer',
                          '& td': {
                            fontWeight: ticket.status === 'open' || ticket.is_unread
                              ? 700
                              : 400
                          }
                        }}
                      >
                        {columns.map(col => (
                          <TableCell
                            key={col.key}
                            style={{
                              ...(colWidths[col.key]
                                ? {
                                    width: colWidths[col.key],
                                    minWidth: colWidths[col.key],
                                    maxWidth: colWidths[col.key],
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }
                                : {}),
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {getTicketValue(
                              ticket,
                              col.key
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                    {!processedTickets.length && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          {loading
                            ? 'Načítavam...'
                            : 'Žiadne tickety'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}
        </section>
      </main>

      <footer className="support-footer">
        <div
          className="support-footer-pattern"
          style={{
            backgroundImage: "url('/pattern_6.png')",
            backgroundRepeat: 'repeat',
            backgroundSize: '400px 400px',
            opacity: 0.1
          }}
        />

        <span>
          © 2026 EnerqiQ Net – All rights reserved
        </span>
      </footer>

      {authorized && (
        <SupportTicketModal
          ticketId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={loadTickets}
        />
      )}

      {!authLoading && !user && showLogin && (
        <SupportLogin
          onClose={() => {
            setShowLogin(false)
            setLoginDismissed(true)
          }}
          onSuccess={() => {
            setShowLogin(false)
            setAccessDenied(false)
          }}
        />
      )}
    </div>
  )
}

export default SupportTickets